import { useMatchMedia } from '@betterment/js-runtime';
import {
  Box,
  Dialog,
  Icon,
  Link,
  Popover,
  Separator,
  ShimmerText,
  Text,
  VStack,
  VisuallyHidden,
} from '@betterment/samba-react';
import { cva } from '@betterment/samba-styled-system/css';

import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { LoadingStatus, PersonalProfile } from './data';
import SidebarButton, { baseUnstyledButtonStyles } from './SidebarButton';

interface ProfileMenuProps {
  personalProfile?: PersonalProfile | null;
  loadingStatus: LoadingStatus;
  reload: () => void;
}

type ProfileMenuVariant = 'mobile' | 'desktop';

const profileMenuNameStyles = cva({
  variants: {
    variant: {
      mobile: { textStyle: 'title200' },
      desktop: { fontWeight: 'medium', fontSize: 'fontSize.100' },
    },
  },
  defaultVariants: { variant: 'desktop' },
});

const profileMenuInfoStyles = cva({
  variants: {
    variant: {
      mobile: { gap: 'space.200', fontSize: 'fontSize.100' },
      desktop: {},
    },
  },
  defaultVariants: { variant: 'desktop' },
});

const profileMenuLinksStyles = cva({
  variants: {
    variant: {
      mobile: { gap: 'space.200', fontSize: 'fontSize.100' },
      desktop: { gap: 'space.100', fontSize: 'fontSize.50' },
    },
  },
  defaultVariants: { variant: 'desktop' },
});

const profileMenuAuthTierStyles = cva({
  variants: {
    variant: {
      mobile: { fontSize: 'fontSize.100' },
      desktop: { fontSize: 'fontSize.50' },
    },
  },
  defaultVariants: { variant: 'desktop' },
});

const profileMenuSeparatorStyles = cva({
  variants: {
    variant: {
      mobile: { my: '0' },
      desktop: { my: 'space.50' },
    },
  },
  defaultVariants: { variant: 'desktop' },
});

const ProfileMenuSidebarButtonContent = ({ personalProfile }: { personalProfile: PersonalProfile }) => (
  <SidebarButton.Content>
    <Text color="text.onPrimary" variant="text50" css={{ fontWeight: 'medium' }}>
      {personalProfile.firstName}
    </Text>
    <Text variant="text25" color="text.onPrimary">
      {personalProfile.jobTitle}
    </Text>
  </SidebarButton.Content>
);

const ProfileMenuContent = ({
  personalProfile,
  variant = 'desktop',
}: {
  personalProfile: PersonalProfile;
  variant?: ProfileMenuVariant;
}) => (
  <VStack css={{ gap: 'space.100' }}>
    <VStack className={profileMenuInfoStyles({ variant })}>
      <Text as="p" className={profileMenuNameStyles({ variant })}>
        {personalProfile.firstName} {personalProfile.lastName}
      </Text>
      <Text>
        {personalProfile.jobTitle}
        <br />
        <Text as="span" color="text.weak" className={profileMenuAuthTierStyles({ variant })}>
          {personalProfile.authorizationTier} access
        </Text>
      </Text>
    </VStack>
    <VStack className={profileMenuLinksStyles({ variant })}>
      <SambaAutoLink to={urlBuilder.profile()}>Betterment at Work profile</SambaAutoLink>
      <Separator className={profileMenuSeparatorStyles({ variant })} />
      {!personalProfile.isAdvisor && (
        <SambaAutoLink
          target="_blank"
          rel="noopener noreferrer"
          to="https://wwws.betterment.com"
          css={{ d: 'flex', alignItems: 'center', gap: 'space.50' }}
        >
          Your employee account <Icon name="Open" size="xsmall" />
        </SambaAutoLink>
      )}
      <Link data-method="delete" href={urlBuilder.signOut()}>
        Log out
      </Link>
    </VStack>
  </VStack>
);

const ProfileMenu = ({ personalProfile, loadingStatus, reload }: ProfileMenuProps) => {
  const isDesktop = useMatchMedia('atLeastMedium');

  if (loadingStatus === 'error') {
    return (
      <SidebarButton onClick={reload} label="Reload profile">
        <SidebarButton.Content showMenuIcon={false}>
          <SidebarButton.ErrorState />
        </SidebarButton.Content>
      </SidebarButton>
    );
  }

  if (loadingStatus === 'loading' || !personalProfile) {
    return (
      <Box css={baseUnstyledButtonStyles}>
        <SidebarButton.Content>
          <VStack data-color-mode="dark" css={{ gap: 'space.50' }}>
            <ShimmerText css={{ maxWidth: '33%' }} textStyle="text25" />
            <ShimmerText css={{ maxWidth: '66%', maxHeight: '14px' }} textStyle="text25" />
          </VStack>
        </SidebarButton.Content>
      </Box>
    );
  }

  if (!isDesktop) {
    return (
      <Dialog>
        <Dialog.Trigger asChild={true}>
          <SidebarButton label="Profile menu">
            <ProfileMenuSidebarButtonContent personalProfile={personalProfile} />
          </SidebarButton>
        </Dialog.Trigger>
        <Dialog.Content
          css={{
            _closed: { animation: 'fadeOut' },
            borderBottomLeftRadius: 'none',
            borderBottomRightRadius: 'none',
            borderTopLeftRadius: 'large',
            borderTopRightRadius: 'large',
            bottom: '0',
            left: '0',
            maxW: '100dvw',
            minW: '0',
            p: 'space.300',
            right: '0',
            top: 'auto',
            transform: 'none',
            w: '100dvw',
            zIndex: 'compat-modal',
          }}
        >
          <VisuallyHidden>
            <Dialog.Title>Profile menu</Dialog.Title>
            <Dialog.Description>View your profile and account options</Dialog.Description>
          </VisuallyHidden>
          <ProfileMenuContent personalProfile={personalProfile} variant="mobile" />
        </Dialog.Content>
      </Dialog>
    );
  }

  return (
    <Popover css={{ zIndex: 5 }}>
      <Popover.Trigger aria-label="Profile menu" css={baseUnstyledButtonStyles}>
        <ProfileMenuSidebarButtonContent personalProfile={personalProfile} />
      </Popover.Trigger>
      <Popover.Content css={{ zIndex: 5, w: '300px' }} side="right" align="end">
        <ProfileMenuContent personalProfile={personalProfile} />
      </Popover.Content>
    </Popover>
  );
};

export default ProfileMenu;
